/**
 * leashPhysics.ts — The elastic-leash spring math, as pure functions.
 *
 * Extracted from Leash.ts (which owns the Phaser sprites and rendering) so the
 * Hooke's-law behaviour can be unit-tested without a canvas. See Leash.ts for how
 * these are wired into the per-frame update, and config/constants.ts → LEASH for
 * the tunable inputs.
 */
import type { Vec2 } from '../utils/vec2';

/** How far the leash is stretched past its slack length (never negative). */
export function leashStretch(dist: number, restLength: number): number {
  return Math.max(0, dist - restLength);
}

/**
 * The spring pull magnitude (px/s) applied along the leash.
 *
 *   raw  = min(stiffness * stretch, maxPull)     // Hooke's F = k·x, capped
 *   pull = max(0, raw - damping * closingSpeed)  // ease off as they already close
 *
 * @param stretch      output of {@link leashStretch}
 * @param stiffness    LEASH.STIFFNESS (px/s of pull per px of stretch)
 * @param maxPull      LEASH.MAX_PULL_SPEED (px/s ceiling)
 * @param damping      LEASH.DAMPING (0 = springy, 1 = stiff)
 * @param closingSpeed relative speed the two are already closing at along the
 *                     leash axis; only positive (closing) values reduce the pull
 */
export function computeLeashPull(
  stretch: number,
  stiffness: number,
  maxPull: number,
  damping: number,
  closingSpeed: number,
): number {
  if (stretch <= 0) return 0;
  const raw = Math.min(stiffness * stretch, maxPull);
  return Math.max(0, raw - damping * Math.max(0, closingSpeed));
}

/**
 * Combine the walker's intended velocity with the leash pull, keeping the walker
 * the stronger of the two. Writes the result into `out` (which may alias `desired`).
 *
 * The dog is allowed to shove the walker around freely *across* their line of
 * travel — that's what makes a lunging dog awkward to walk with. What it may not
 * do is stop them: the component of the result **along** the walker's steering
 * direction is floored at `minProgress` × their intended speed, so a hard pull
 * turns a direction into a slog rather than a wall, and can never reverse them.
 *
 * With no steering input there's no direction to protect, so the pull applies in
 * full and a determined dog can tow a standing walker — which, now that nothing
 * else can slow the walker down, is the only way the dog gets to move them
 * where they didn't choose to go.
 *
 * @param desired     the walker's own input velocity (units/s)
 * @param pullX       leash pull already scaled by LEASH.PLAYER_MASS_FACTOR
 * @param pullY       ditto, y component
 * @param minProgress LEASH.MIN_PLAYER_PROGRESS (0 = the dog can stall you, 1 = it can't slow you)
 */
export function resistLeashPull(
  desired: Vec2,
  pullX: number,
  pullY: number,
  minProgress: number,
  out: Vec2,
): Vec2 {
  const desiredSpeed = Math.hypot(desired.x, desired.y);
  const combinedX = desired.x + pullX;
  const combinedY = desired.y + pullY;

  if (desiredSpeed === 0) {
    // Standing still: nothing to defend, the dog gets its way.
    out.x = combinedX;
    out.y = combinedY;
    return out;
  }

  // Split the combined velocity into "along where I'm steering" + "sideways",
  // raise the along-component to the floor, and reassemble.
  const ux = desired.x / desiredSpeed;
  const uy = desired.y / desiredSpeed;
  const along = combinedX * ux + combinedY * uy;
  const floor = minProgress * desiredSpeed;

  out.x = combinedX;
  out.y = combinedY;
  if (along < floor) {
    const deficit = floor - along;
    out.x += ux * deficit;
    out.y += uy * deficit;
  }
  return out;
}
